import { determineRdProjectEligibility, EligibilityAnswers } from '@common/neo.tax';
import { Stack } from '@ui-components/neo.tax';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { RdProject, useUpdateRdProjectEligibility } from '@neo.tax/rd/hooks';
import { getAiGuidanceBadgeConfig } from '@neo.tax/rd/utils';

import { Confirmation } from './Confirmation';
import { Header } from './Header';
import { Question } from './Question';
import { Questions } from './rd-project-eligibility-review.utils';

const MAX_REASONING_LENGTH = 180;
const NEEDS_REVIEW_STATUS = 'Needs Review';

type RdProjectEligibilityReviewProps = {
  rdProject: RdProject;
};

export const RdProjectEligibilityReview = ({ rdProject }: RdProjectEligibilityReviewProps): ReactElement => {
  // get badge config
  const aiGuidanceBadgeConfig = useMemo(
    () => getAiGuidanceBadgeConfig(rdProject.aiGuidance, rdProject.rdDetermination),
    [rdProject.aiGuidance, rdProject.rdDetermination],
  );

  // determine if the ai guidance reasoning is too long and should be expandable
  const reasoning = useMemo(() => aiGuidanceBadgeConfig?.description || '', [aiGuidanceBadgeConfig?.description]);
  const shouldTruncateReasoning = useMemo(() => reasoning.length > MAX_REASONING_LENGTH, [reasoning]);

  // update rd project eligibility
  const [updateRdProjectEligibility] = useUpdateRdProjectEligibility();
  // track the loading state of the confirmation modal
  const [isConfirmationLoading, setIsConfirmationLoading] = useState<false | 'BACK' | 'SAVE'>(false);

  // determine if the review questionnaire is open
  const [isReviewQuestionnaireOpen, setReviewQuestionnaireOpen] = useState(false);

  const [currentQuestionnaireAnswers, setCurrentQuestionnaireAnswers] = useState<EligibilityAnswers>({
    isTechnicallyUncertain: null,
    isCustomerFacingDevelopment: null,
    isInnovativeAndRisky: null,
  });

  // determine the status of the questions
  const questionStatus = useMemo(() => {
    if (rdProject.aiGuidance !== NEEDS_REVIEW_STATUS) return null;
    return determineRdProjectEligibility(currentQuestionnaireAnswers);
  }, [rdProject.aiGuidance, currentQuestionnaireAnswers]);

  // initialize questions
  const currentQuestion = useMemo(() => {
    if (!questionStatus) return undefined;
    if (questionStatus.status === 'complete') return undefined;
    return Questions[questionStatus.currentQuestionIndex - 1];
  }, [questionStatus]);

  // A qualifying outcome inherits its Technical Challenges / Process of Experimentation from the
  // determination, but a project sent to review for missing uncertainty has neither — warn on the
  // confirmation screen that its R&D Details will stay Incomplete until a human fills them in.
  const showIncompleteNarrativeNote = useMemo(() => {
    if (!questionStatus || questionStatus.status !== 'complete' || questionStatus.outcome !== 'ELIGIBLE') return false;
    return !rdProject.technicalChallenges || !rdProject.processOfExperimentation;
  }, [questionStatus, rdProject.technicalChallenges, rdProject.processOfExperimentation]);

  // determine if the project needs review
  const showConfirmProjectDetails = useMemo(() => {
    // project is not a "Needs Review" project
    if (rdProject.aiGuidance !== NEEDS_REVIEW_STATUS) return false;

    // questionnaire is completed
    if (!questionStatus || questionStatus.status !== 'pending') return false;

    // if still on the first question and not previously opened, show the confirm project details link
    return questionStatus.currentQuestionIndex === 1 && !isReviewQuestionnaireOpen ? true : false;
  }, [rdProject.aiGuidance, questionStatus, isReviewQuestionnaireOpen]);

  // reset modal when project changes
  useEffect(() => {
    // reset questionnaire answers to match the new project
    const newAnswers: EligibilityAnswers = {
      isTechnicallyUncertain: rdProject.isTechnicallyUncertain ?? null,
      isCustomerFacingDevelopment: rdProject.isCustomerFacingDevelopment ?? null,
      isInnovativeAndRisky: rdProject.isInnovativeAndRisky ?? null,
    };
    setCurrentQuestionnaireAnswers(newAnswers);

    // reset the review questionnaire when the component loads a new project
    setReviewQuestionnaireOpen(false);
  }, [
    rdProject.id,
    rdProject.aiGuidance,
    rdProject.isTechnicallyUncertain,
    rdProject.isCustomerFacingDevelopment,
    rdProject.isInnovativeAndRisky,
  ]);

  useEffect(() => {
    // open the review questionnaire if:
    // - the project is a "Needs Review" project
    // - the user hasn't finished the review questionnaire
    // - the user is not on the first question
    if (
      rdProject.aiGuidance === NEEDS_REVIEW_STATUS &&
      questionStatus &&
      questionStatus.status === 'pending' &&
      // if not on the first question, open the review questionnaire
      questionStatus.currentQuestionIndex > 1
    ) {
      setReviewQuestionnaireOpen(true);
    }
  }, [rdProject.aiGuidance, questionStatus]);

  // when the user answers a question, update the rd project
  const onSaveAnswer = useCallback(
    async (data: Partial<RdProject>): Promise<void> => {
      await updateRdProjectEligibility(rdProject.id, data, {
        onCompleted: () => {
          setIsConfirmationLoading(false);
        },
        onError: () => {
          setIsConfirmationLoading(false);
        },
        context: {
          successToast: {
            title: 'AI Guidance review saved',
          },
          errorToast: {
            title: 'Error saving answer',
            description: 'Something unexpected prevented your changes from saving. Please try again.',
          },
        },
      });
    },
    [updateRdProjectEligibility, rdProject.id],
  );

  const handleConfirmProjectDetails = useCallback((): void => {
    setReviewQuestionnaireOpen(true);
  }, [setReviewQuestionnaireOpen]);

  const handleYes = useCallback((): void => {
    if (!currentQuestion) return;
    setCurrentQuestionnaireAnswers({ ...currentQuestionnaireAnswers, ...currentQuestion?.YesResult });
  }, [currentQuestion, currentQuestionnaireAnswers]);

  const handleNo = useCallback((): void => {
    if (!currentQuestion) return;
    setCurrentQuestionnaireAnswers({ ...currentQuestionnaireAnswers, ...currentQuestion?.NoResult });
  }, [currentQuestion, currentQuestionnaireAnswers]);

  const handleBack = useCallback((): void => {
    if (!questionStatus) return;

    // if the question is pending, we need to reset the previous question
    // if the question is complete, we need to reset the current question
    const index = questionStatus.status === 'pending' ? 2 : 1;
    const previousQuestion = Questions[questionStatus.currentQuestionIndex - index];

    setCurrentQuestionnaireAnswers({ ...currentQuestionnaireAnswers, ...previousQuestion?.Reset });
  }, [questionStatus, currentQuestionnaireAnswers]);

  const handleSave = useCallback((): void => {
    setIsConfirmationLoading('SAVE');
    onSaveAnswer(currentQuestionnaireAnswers);
  }, [onSaveAnswer, setIsConfirmationLoading, currentQuestionnaireAnswers]);

  if (!aiGuidanceBadgeConfig) return <></>;

  return (
    <Stack direction="column" gap={0}>
      <Header
        projectBadgeConfig={aiGuidanceBadgeConfig}
        isOpen={isReviewQuestionnaireOpen}
        description={reasoning}
        showMoreLessLink={shouldTruncateReasoning}
        showConfirmProjectDetailsLink={showConfirmProjectDetails}
        onClickConfirmProjectDetails={handleConfirmProjectDetails}
      />
      {isReviewQuestionnaireOpen && questionStatus && questionStatus.status === 'pending' && currentQuestion && (
        <Question
          totalQuestions={Questions.length}
          questionNumber={questionStatus.currentQuestionIndex}
          text={currentQuestion.text}
          onYes={handleYes}
          onNo={handleNo}
          onBack={handleBack}
          isShowBackButton={questionStatus.currentQuestionIndex > 1}
        />
      )}
      {isReviewQuestionnaireOpen && questionStatus && questionStatus.status === 'complete' && (
        <Confirmation
          isLoading={isConfirmationLoading}
          onBack={handleBack}
          onSave={handleSave}
          currentAnswers={currentQuestionnaireAnswers}
          showIncompleteNarrativeNote={showIncompleteNarrativeNote}
        />
      )}
    </Stack>
  );
};
